/**
 * UNIFIED AGENT STATUS
 *
 * Maps heterogeneous status types from chat sessions, chat tabs,
 * terminal tabs, and review runs into a single enum for the
 * My Agents unified view.
 */

import {
  Loader2,
  Circle,
  Eye,
  CheckCircle,
  AlertCircle,
  Clock,
  type LucideIcon,
} from "lucide-react";
import type { SessionState } from "@/types/ai-chat";
import type { ChatTabStatus } from "@/stores/chatTabStore";
import type { TerminalActivityStatus } from "@/stores/localTerminalStore";
import type { RunStatus } from "@/stores/reviewStore";
import type { AgentState, AgentAttentionState } from "@/stores/worldStore";

// ── Unified status enum ────────────────────────────────────────────

export type UnifiedAgentStatus =
  | "running"
  | "idle"
  | "waiting_review"
  | "completed"
  | "error"
  | "stale";

// ── Status display config ──────────────────────────────────────────

export const STATUS_CONFIG: Record<UnifiedAgentStatus, { label: string; color: string; dotClass: string }> = {
  running:        { label: "Running", color: "var(--colony-blue)",           dotClass: "bg-[var(--colony-blue)] animate-pulse" },
  idle:           { label: "Idle",    color: "var(--colony-text-tertiary)",  dotClass: "bg-[var(--colony-text-tertiary)]" },
  waiting_review: { label: "Review",  color: "var(--colony-amber)",          dotClass: "bg-[var(--colony-amber)]" },
  completed:      { label: "Done",    color: "var(--colony-green)",          dotClass: "bg-[var(--colony-green)]" },
  error:          { label: "Error",   color: "var(--colony-red)",            dotClass: "bg-[var(--colony-red)]" },
  stale:          { label: "Stale",   color: "var(--colony-text-tertiary)",  dotClass: "bg-[var(--colony-text-tertiary)]" },
};

export const NEEDS_ATTENTION_STATUSES: ReadonlySet<UnifiedAgentStatus> = new Set<UnifiedAgentStatus>([
  "error",
  "waiting_review",
]);

export function needsAttention(status: UnifiedAgentStatus): boolean {
  return NEEDS_ATTENTION_STATUSES.has(status);
}

// ── Status predicates ──────────────────────────────────────────────

export function isActive(status: UnifiedAgentStatus): boolean {
  return status === "running";
}

const TERMINAL_STATUSES: ReadonlySet<UnifiedAgentStatus> = new Set<UnifiedAgentStatus>([
  "completed",
  "error",
  "stale",
]);

export function isTerminal(status: UnifiedAgentStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

// Single source for the status icon mapping (mirrors the card's classNames from
// STATUS_CONFIG). Matches the icons the UnifiedAgentCard previously inlined.
const STATUS_ICONS: Record<UnifiedAgentStatus, LucideIcon> = {
  running: Loader2,
  idle: Circle,
  waiting_review: Eye,
  completed: CheckCircle,
  error: AlertCircle,
  stale: Clock,
};

export function statusIcon(status: UnifiedAgentStatus): LucideIcon {
  return STATUS_ICONS[status];
}

// ── Mapping functions ──────────────────────────────────────────────

const SESSION_STATE_MAP: Record<SessionState, UnifiedAgentStatus> = {
  idle:      "idle",
  running:   "running",
  reviewing: "waiting_review",
  completed: "completed",
  error:     "error",
};

export function chatSessionToUnifiedStatus(state: SessionState): UnifiedAgentStatus {
  return SESSION_STATE_MAP[state] ?? "idle";
}

const CHAT_TAB_MAP: Record<ChatTabStatus, UnifiedAgentStatus> = {
  idle:      "idle",
  running:   "running",
  completed: "completed",
  error:     "error",
  stale:     "stale",
};

export function chatTabToUnifiedStatus(status: ChatTabStatus): UnifiedAgentStatus {
  return CHAT_TAB_MAP[status] ?? "idle";
}

const TERMINAL_ACTIVITY_MAP: Record<TerminalActivityStatus, UnifiedAgentStatus> = {
  active:       "running",
  idle:         "idle",
  disconnected: "completed",
  error:        "error",
};

export function terminalToUnifiedStatus(activityStatus: TerminalActivityStatus): UnifiedAgentStatus {
  return TERMINAL_ACTIVITY_MAP[activityStatus] ?? "idle";
}

const RUN_STATUS_MAP: Record<RunStatus, UnifiedAgentStatus> = {
  pending:      "idle",
  running:      "running",
  needs_review: "waiting_review",
  ready:        "waiting_review",
  blocked:      "error",
  merged:       "completed",
  failed:       "error",
};

export function reviewRunToUnifiedStatus(status: RunStatus): UnifiedAgentStatus {
  return RUN_STATUS_MAP[status] ?? "idle";
}

/**
 * Map a unified agent status to how it renders on a World building (orbiting
 * avatar + busy badge). Returns null for statuses that aren't actively working
 * (completed / stale) so finished agents don't keep orbiting.
 */
export function unifiedStatusToAgentVisuals(
  status: UnifiedAgentStatus,
): { state: AgentState; attentionState?: AgentAttentionState } | null {
  switch (status) {
    case "running":        return { state: "busy" };
    case "waiting_review": return { state: "busy", attentionState: "human_needed" };
    case "idle":           return { state: "idle" };
    case "error":          return { state: "idle", attentionState: "error" };
    default:               return null;
  }
}
